(() => {
  const propertyName = 'Pre-order';
  const clearedStateProperty = '_cct_stock_checked';
  const stateCache = new Map();
  const cacheTtl = 30000;
  let timer;
  let runningPromise;
  let rerun = false;
  let checkoutPending = false;

  const rootUrl = window.Shopify?.routes?.root || '/';

  function cartUrl(path) {
    return `${rootUrl}${path}`.replace(/\/\/{2,}/g, '/');
  }

  async function getCart() {
    const response = await fetch(cartUrl('cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
    return response.json();
  }

  async function getPreorderState(item) {
    const key = String(item.variant_id);
    const cached = stateCache.get(key);
    if (cached && Date.now() - cached.time < cacheTtl) return cached.value;

    const productUrl = new URL(item.url, window.location.origin);
    productUrl.searchParams.set('variant', key);
    productUrl.searchParams.set('section_id', 'main-product');

    const response = await fetch(productUrl.toString(), {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) return null;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const marker = doc.querySelector(`[data-cct-preorder-marker][data-variant-id="${CSS.escape(key)}"]`);
    if (!marker) return null;

    const value = {
      isPreorder: marker.dataset.isPreorder === 'true',
      message: marker.dataset.message || 'Contact us for current ETA'
    };
    stateCache.set(key, { time: Date.now(), value });
    return value;
  }

  async function changeProperties(item, state) {
    const properties = { ...(item.properties || {}) };
    if (state.isPreorder) {
      properties[propertyName] = state.message;
      delete properties[clearedStateProperty];
    } else {
      delete properties[propertyName];
      // Shopify ignores clearing the final property with an empty object.
      if (Object.keys(properties).length === 0) properties[clearedStateProperty] = '1';
    }

    const response = await fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties
      })
    });
    if (!response.ok) throw new Error(`Cart line update failed (${response.status})`);
    await response.json();
  }

  function decorateAssortionProperties() {
    document.querySelectorAll('.ast-cart__item__property').forEach((property) => {
      const label = property.querySelector('.ast-cart__item__property-name');
      if (!label || label.textContent.trim().replace(/:\s*$/, '') !== propertyName) return;
      if (!property.classList.contains('cct-preorder-property')) {
        property.classList.add('cct-preorder-property');
      }
      if (label.textContent !== propertyName) label.textContent = propertyName;
    });
  }

  async function reconcile() {
    if (runningPromise) {
      rerun = true;
      return runningPromise;
    }

    runningPromise = (async () => {
      try {
        for (let pass = 0; pass < 20; pass += 1) {
          const cart = await getCart();
          const states = await Promise.all(cart.items.map((item) => getPreorderState(item)));
          const index = cart.items.findIndex((item, itemIndex) => {
            const state = states[itemIndex];
            if (!state) return false;
            const hasPreorderProperty = Object.prototype.hasOwnProperty.call(item.properties || {}, propertyName);
            if (!state.isPreorder) return hasPreorderProperty;
            return item.properties?.[propertyName] !== state.message;
          });

          if (index === -1) break;
          await changeProperties(cart.items[index], states[index]);
        }
      } catch (error) {
        console.warn('[CCT Pre-order] Cart disclosure could not be refreshed.', error);
      } finally {
        decorateAssortionProperties();
      }
    })();

    await runningPromise;
    runningPromise = undefined;
    if (rerun) {
      rerun = false;
      scheduleReconcile(100);
    }
  }

  function scheduleReconcile(delay = 150) {
    window.clearTimeout(timer);
    timer = window.setTimeout(reconcile, delay);
  }

  document.addEventListener('cart:updated', () => scheduleReconcile());
  document.addEventListener('on:cart:add', () => scheduleReconcile());
  document.addEventListener('on:line-item:change', () => scheduleReconcile());

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('.ast-cart__checkout');
    if (!button || checkoutPending || button.dataset.cctPreorderReady === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutPending = true;
    await reconcile();
    checkoutPending = false;
    button.dataset.cctPreorderReady = 'true';
    button.click();
  }, true);

  const observer = new MutationObserver(decorateAssortionProperties);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleReconcile(0), { once: true });
  } else {
    scheduleReconcile(0);
  }
})();
